// File: /opt/dev_environment/assets/workspace/app-init/src/constants/verbData.js

/**
 * Verified conjugation data for Top 10 and Top 11-20 Italian verbs.
 * Each verb includes both Italian AND German conjugations for all forms.
 *
 * Person order: io, tu, lui/lei, noi, voi, loro
 * German order: ich, du, er/sie, wir, ihr, sie
 */

export const CONJUGATION_FORMS = [
  { value: 'presente', label: 'Presente', germanLabel: 'Präsens', personsCount: 6 },
  { value: 'imperfetto', label: 'Imperfetto', germanLabel: 'Imperfekt', personsCount: 6 },
  { value: 'condizionale', label: 'Condizionale', germanLabel: 'Konditional', personsCount: 6 },
  { value: 'futuroSemplice', label: 'Futuro Semplice', germanLabel: 'Futur I', personsCount: 6 },
  { value: 'participioPassato', label: 'Participio Passato', germanLabel: 'Partizip Perfekt', personsCount: 1 },
  { value: 'futuro_semplice', label: 'Futuro Semplice', germanLabel: 'Futur I', personsCount: 6 },
  { value: 'condizionale_presente', label: 'Condizionale', germanLabel: 'Konditional', personsCount: 6 },
  { value: 'passato_prossimo', label: 'Participio Passato', germanLabel: 'Partizip Perfekt', personsCount: 1 },
];

export const PERSONS = [
  { value: '1s', label: '1. Person Singular (io)', germanLabel: 'ich' },
  { value: '2s', label: '2. Person Singular (tu)', germanLabel: 'du' },
  { value: '3s', label: '3. Person Singular (lui/lei)', germanLabel: 'er/sie/es' },
  { value: '1p', label: '1. Person Plural (noi)', germanLabel: 'wir' },
  { value: '2p', label: '2. Person Plural (voi)', germanLabel: 'ihr' },
  { value: '3p', label: '3. Person Plural (loro)', germanLabel: 'sie (Plural)' },
];

export const VERB_GROUPS = [
  { value: 'top10', label: 'Top 10 Verben', enabled: true },
  { value: 'top11_20', label: 'Top 11–20 Verben', enabled: true },
  { value: 'top21_50', label: 'Top 21–50 Verben', enabled: false },
];

/**
 * Top 10 most frequent Italian verbs with full conjugation tables
 * including German translations for every conjugated form.
 */
const TOP_10_VERBS = [
  {
    infinitive: 'essere',
    germanInfinitive: 'sein',
    group: 'top10',
    conjugations: {
      presente: {
        it: ['sono', 'sei', 'è', 'siamo', 'siete', 'sono'],
        de: ['ich bin', 'du bist', 'er/sie ist', 'wir sind', 'ihr seid', 'sie sind'],
      },
      imperfetto: {
        it: ['ero', 'eri', 'era', 'eravamo', 'eravate', 'erano'],
        de: ['ich war', 'du warst', 'er/sie war', 'wir waren', 'ihr wart', 'sie waren'],
      },
      condizionale: {
        it: ['sarei', 'saresti', 'sarebbe', 'saremmo', 'sareste', 'sarebbero'],
        de: ['ich wäre', 'du wärst', 'er/sie wäre', 'wir wären', 'ihr wärt', 'sie wären'],
      },
      futuroSemplice: {
        it: ['sarò', 'sarai', 'sarà', 'saremo', 'sarete', 'saranno'],
        de: ['ich werde sein', 'du wirst sein', 'er/sie wird sein', 'wir werden sein', 'ihr werdet sein', 'sie werden sein'],
      },
      participioPassato: {
        it: ['stato'],
        de: ['gewesen'],
      },
    },
  },
  {
    infinitive: 'avere',
    germanInfinitive: 'haben',
    group: 'top10',
    conjugations: {
      presente: {
        it: ['ho', 'hai', 'ha', 'abbiamo', 'avete', 'hanno'],
        de: ['ich habe', 'du hast', 'er/sie hat', 'wir haben', 'ihr habt', 'sie haben'],
      },
      imperfetto: {
        it: ['avevo', 'avevi', 'aveva', 'avevamo', 'avevate', 'avevano'],
        de: ['ich hatte', 'du hattest', 'er/sie hatte', 'wir hatten', 'ihr hattet', 'sie hatten'],
      },
      condizionale: {
        it: ['avrei', 'avresti', 'avrebbe', 'avremmo', 'avreste', 'avrebbero'],
        de: ['ich hätte', 'du hättest', 'er/sie hätte', 'wir hätten', 'ihr hättet', 'sie hätten'],
      },
      futuroSemplice: {
        it: ['avrò', 'avrai', 'avrà', 'avremo', 'avrete', 'avranno'],
        de: ['ich werde haben', 'du wirst haben', 'er/sie wird haben', 'wir werden haben', 'ihr werdet haben', 'sie werden haben'],
      },
      participioPassato: {
        it: ['avuto'],
        de: ['gehabt'],
      },
    },
  },
  {
    infinitive: 'potere',
    germanInfinitive: 'können',
    group: 'top10',
    conjugations: {
      presente: {
        it: ['posso', 'puoi', 'può', 'possiamo', 'potete', 'possono'],
        de: ['ich kann', 'du kannst', 'er/sie kann', 'wir können', 'ihr könnt', 'sie können'],
      },
      imperfetto: {
        it: ['potevo', 'potevi', 'poteva', 'potevamo', 'potevate', 'potevano'],
        de: ['ich konnte', 'du konntest', 'er/sie konnte', 'wir konnten', 'ihr konntet', 'sie konnten'],
      },
      condizionale: {
        it: ['potrei', 'potresti', 'potrebbe', 'potremmo', 'potreste', 'potrebbero'],
        de: ['ich könnte', 'du könntest', 'er/sie könnte', 'wir könnten', 'ihr könntet', 'sie könnten'],
      },
      futuroSemplice: {
        it: ['potrò', 'potrai', 'potrà', 'potremo', 'potrete', 'potranno'],
        de: ['ich werde können', 'du wirst können', 'er/sie wird können', 'wir werden können', 'ihr werdet können', 'sie werden können'],
      },
      participioPassato: {
        it: ['potuto'],
        de: ['gekonnt'],
      },
    },
  },
  {
    infinitive: 'fare',
    germanInfinitive: 'machen, tun',
    group: 'top10',
    conjugations: {
      presente: {
        it: ['faccio', 'fai', 'fa', 'facciamo', 'fate', 'fanno'],
        de: ['ich mache', 'du machst', 'er/sie macht', 'wir machen', 'ihr macht', 'sie machen'],
      },
      imperfetto: {
        it: ['facevo', 'facevi', 'faceva', 'facevamo', 'facevate', 'facevano'],
        de: ['ich machte', 'du machtest', 'er/sie machte', 'wir machten', 'ihr machtet', 'sie machten'],
      },
      condizionale: {
        it: ['farei', 'faresti', 'farebbe', 'faremmo', 'fareste', 'farebbero'],
        de: ['ich würde machen', 'du würdest machen', 'er/sie würde machen', 'wir würden machen', 'ihr würdet machen', 'sie würden machen'],
      },
      futuroSemplice: {
        it: ['farò', 'farai', 'farà', 'faremo', 'farete', 'faranno'],
        de: ['ich werde machen', 'du wirst machen', 'er/sie wird machen', 'wir werden machen', 'ihr werdet machen', 'sie werden machen'],
      },
      participioPassato: {
        it: ['fatto'],
        de: ['gemacht'],
      },
    },
  },
  {
    infinitive: 'dire',
    germanInfinitive: 'sagen',
    group: 'top10',
    conjugations: {
      presente: {
        it: ['dico', 'dici', 'dice', 'diciamo', 'dite', 'dicono'],
        de: ['ich sage', 'du sagst', 'er/sie sagt', 'wir sagen', 'ihr sagt', 'sie sagen'],
      },
      imperfetto: {
        it: ['dicevo', 'dicevi', 'diceva', 'dicevamo', 'dicevate', 'dicevano'],
        de: ['ich sagte', 'du sagtest', 'er/sie sagte', 'wir sagten', 'ihr sagtet', 'sie sagten'],
      },
      condizionale: {
        it: ['direi', 'diresti', 'direbbe', 'diremmo', 'direste', 'direbbero'],
        de: ['ich würde sagen', 'du würdest sagen', 'er/sie würde sagen', 'wir würden sagen', 'ihr würdet sagen', 'sie würden sagen'],
      },
      futuroSemplice: {
        it: ['dirò', 'dirai', 'dirà', 'diremo', 'direte', 'diranno'],
        de: ['ich werde sagen', 'du wirst sagen', 'er/sie wird sagen', 'wir werden sagen', 'ihr werdet sagen', 'sie werden sagen'],
      },
      participioPassato: {
        it: ['detto'],
        de: ['gesagt'],
      },
    },
  },
  {
    infinitive: 'volere',
    germanInfinitive: 'wollen',
    group: 'top10',
    conjugations: {
      presente: {
        it: ['voglio', 'vuoi', 'vuole', 'vogliamo', 'volete', 'vogliono'],
        de: ['ich will', 'du willst', 'er/sie will', 'wir wollen', 'ihr wollt', 'sie wollen'],
      },
      imperfetto: {
        it: ['volevo', 'volevi', 'voleva', 'volevamo', 'volevate', 'volevano'],
        de: ['ich wollte', 'du wolltest', 'er/sie wollte', 'wir wollten', 'ihr wolltet', 'sie wollten'],
      },
      condizionale: {
        it: ['vorrei', 'vorresti', 'vorrebbe', 'vorremmo', 'vorreste', 'vorrebbero'],
        de: ['ich würde wollen', 'du würdest wollen', 'er/sie würde wollen', 'wir würden wollen', 'ihr würdet wollen', 'sie würden wollen'],
      },
      futuroSemplice: {
        it: ['vorrò', 'vorrai', 'vorrà', 'vorremo', 'vorrete', 'vorranno'],
        de: ['ich werde wollen', 'du wirst wollen', 'er/sie wird wollen', 'wir werden wollen', 'ihr werdet wollen', 'sie werden wollen'],
      },
      participioPassato: {
        it: ['voluto'],
        de: ['gewollt'],
      },
    },
  },
  {
    infinitive: 'dovere',
    germanInfinitive: 'müssen, sollen',
    group: 'top10',
    conjugations: {
      presente: {
        it: ['devo', 'devi', 'deve', 'dobbiamo', 'dovete', 'devono'],
        de: ['ich muss', 'du musst', 'er/sie muss', 'wir müssen', 'ihr müsst', 'sie müssen'],
      },
      imperfetto: {
        it: ['dovevo', 'dovevi', 'doveva', 'dovevamo', 'dovevate', 'dovevano'],
        de: ['ich musste', 'du musstest', 'er/sie musste', 'wir mussten', 'ihr musstet', 'sie mussten'],
      },
      condizionale: {
        it: ['dovrei', 'dovresti', 'dovrebbe', 'dovremmo', 'dovreste', 'dovrebbero'],
        de: ['ich müsste', 'du müsstest', 'er/sie müsste', 'wir müssten', 'ihr müsstet', 'sie müssten'],
      },
      futuroSemplice: {
        it: ['dovrò', 'dovrai', 'dovrà', 'dovremo', 'dovrete', 'dovranno'],
        de: ['ich werde müssen', 'du wirst müssen', 'er/sie wird müssen', 'wir werden müssen', 'ihr werdet müssen', 'sie werden müssen'],
      },
      participioPassato: {
        it: ['dovuto'],
        de: ['gemusst'],
      },
    },
  },
  {
    infinitive: 'venire',
    germanInfinitive: 'kommen',
    group: 'top10',
    conjugations: {
      presente: {
        it: ['vengo', 'vieni', 'viene', 'veniamo', 'venite', 'vengono'],
        de: ['ich komme', 'du kommst', 'er/sie kommt', 'wir kommen', 'ihr kommt', 'sie kommen'],
      },
      imperfetto: {
        it: ['venivo', 'venivi', 'veniva', 'venivamo', 'venivate', 'venivano'],
        de: ['ich kam', 'du kamst', 'er/sie kam', 'wir kamen', 'ihr kamt', 'sie kamen'],
      },
      condizionale: {
        it: ['verrei', 'verresti', 'verrebbe', 'verremmo', 'verreste', 'verrebbero'],
        de: ['ich würde kommen', 'du würdest kommen', 'er/sie würde kommen', 'wir würden kommen', 'ihr würdet kommen', 'sie würden kommen'],
      },
      futuroSemplice: {
        it: ['verrò', 'verrai', 'verrà', 'verremo', 'verrete', 'verranno'],
        de: ['ich werde kommen', 'du wirst kommen', 'er/sie wird kommen', 'wir werden kommen', 'ihr werdet kommen', 'sie werden kommen'],
      },
      participioPassato: {
        it: ['venuto'],
        de: ['gekommen'],
      },
    },
  },
  {
    infinitive: 'andare',
    germanInfinitive: 'gehen',
    group: 'top10',
    conjugations: {
      presente: {
        it: ['vado', 'vai', 'va', 'andiamo', 'andate', 'vanno'],
        de: ['ich gehe', 'du gehst', 'er/sie geht', 'wir gehen', 'ihr geht', 'sie gehen'],
      },
      imperfetto: {
        it: ['andavo', 'andavi', 'andava', 'andavamo', 'andavate', 'andavano'],
        de: ['ich ging', 'du gingst', 'er/sie ging', 'wir gingen', 'ihr gingt', 'sie gingen'],
      },
      condizionale: {
        it: ['andrei', 'andresti', 'andrebbe', 'andremmo', 'andreste', 'andrebbero'],
        de: ['ich würde gehen', 'du würdest gehen', 'er/sie würde gehen', 'wir würden gehen', 'ihr würdet gehen', 'sie würden gehen'],
      },
      futuroSemplice: {
        it: ['andrò', 'andrai', 'andrà', 'andremo', 'andrete', 'andranno'],
        de: ['ich werde gehen', 'du wirst gehen', 'er/sie wird gehen', 'wir werden gehen', 'ihr werdet gehen', 'sie werden gehen'],
      },
      participioPassato: {
        it: ['andato'],
        de: ['gegangen'],
      },
    },
  },
  {
    infinitive: 'stare',
    germanInfinitive: 'bleiben, sich befinden',
    group: 'top10',
    conjugations: {
      presente: {
        it: ['sto', 'stai', 'sta', 'stiamo', 'state', 'stanno'],
        de: ['ich bleibe', 'du bleibst', 'er/sie bleibt', 'wir bleiben', 'ihr bleibt', 'sie bleiben'],
      },
      imperfetto: {
        it: ['stavo', 'stavi', 'stava', 'stavamo', 'stavate', 'stavano'],
        de: ['ich blieb', 'du bliebst', 'er/sie blieb', 'wir blieben', 'ihr bliebt', 'sie blieben'],
      },
      condizionale: {
        it: ['starei', 'staresti', 'starebbe', 'staremmo', 'stareste', 'starebbero'],
        de: ['ich würde bleiben', 'du würdest bleiben', 'er/sie würde bleiben', 'wir würden bleiben', 'ihr würdet bleiben', 'sie würden bleiben'],
      },
      futuroSemplice: {
        it: ['starò', 'starai', 'starà', 'staremo', 'starete', 'staranno'],
        de: ['ich werde bleiben', 'du wirst bleiben', 'er/sie wird bleiben', 'wir werden bleiben', 'ihr werdet bleiben', 'sie werden bleiben'],
      },
      participioPassato: {
        it: ['stato'],
        de: ['geblieben'],
      },
    },
  },
];

/**
 * Top 11-20 most frequent Italian verbs.
 */
const TOP_11_20_VERBS = [
  {
    infinitive: 'dare',
    germanInfinitive: 'geben',
    group: 'top11_20',
    conjugations: {
      presente: {
        it: ['do', 'dai', 'dà', 'diamo', 'date', 'danno'],
        de: ['ich gebe', 'du gibst', 'er/sie gibt', 'wir geben', 'ihr gebt', 'sie geben'],
      },
      imperfetto: {
        it: ['davo', 'davi', 'dava', 'davamo', 'davate', 'davano'],
        de: ['ich gab', 'du gabst', 'er/sie gab', 'wir gaben', 'ihr gabt', 'sie gaben'],
      },
      condizionale: {
        it: ['darei', 'daresti', 'darebbe', 'daremmo', 'dareste', 'darebbero'],
        de: ['ich würde geben', 'du würdest geben', 'er/sie würde geben', 'wir würden geben', 'ihr würdet geben', 'sie würden geben'],
      },
      futuroSemplice: {
        it: ['darò', 'darai', 'darà', 'daremo', 'darete', 'daranno'],
        de: ['ich werde geben', 'du wirst geben', 'er/sie wird geben', 'wir werden geben', 'ihr werdet geben', 'sie werden geben'],
      },
      participioPassato: {
        it: ['dato'],
        de: ['gegeben'],
      },
    },
  },
  {
    infinitive: 'sapere',
    germanInfinitive: 'wissen',
    group: 'top11_20',
    conjugations: {
      presente: {
        it: ['so', 'sai', 'sa', 'sappiamo', 'sapete', 'sanno'],
        de: ['ich weiß', 'du weißt', 'er/sie weiß', 'wir wissen', 'ihr wisst', 'sie wissen'],
      },
      imperfetto: {
        it: ['sapevo', 'sapevi', 'sapeva', 'sapevamo', 'sapevate', 'sapevano'],
        de: ['ich wusste', 'du wusstest', 'er/sie wusste', 'wir wussten', 'ihr wusstet', 'sie wussten'],
      },
      condizionale: {
        it: ['saprei', 'sapresti', 'saprebbe', 'sapremmo', 'sapreste', 'saprebbero'],
        de: ['ich würde wissen', 'du würdest wissen', 'er/sie würde wissen', 'wir würden wissen', 'ihr würdet wissen', 'sie würden wissen'],
      },
      futuroSemplice: {
        it: ['saprò', 'saprai', 'saprà', 'sapremo', 'saprete', 'sapranno'],
        de: ['ich werde wissen', 'du wirst wissen', 'er/sie wird wissen', 'wir werden wissen', 'ihr werdet wissen', 'sie werden wissen'],
      },
      participioPassato: {
        it: ['saputo'],
        de: ['gewusst'],
      },
    },
  },
  {
    infinitive: 'vedere',
    germanInfinitive: 'sehen',
    group: 'top11_20',
    conjugations: {
      presente: {
        it: ['vedo', 'vedi', 'vede', 'vediamo', 'vedete', 'vedono'],
        de: ['ich sehe', 'du siehst', 'er/sie sieht', 'wir sehen', 'ihr seht', 'sie sehen'],
      },
      imperfetto: {
        it: ['vedevo', 'vedevi', 'vedeva', 'vedevamo', 'vedevate', 'vedevano'],
        de: ['ich sah', 'du sahst', 'er/sie sah', 'wir sahen', 'ihr saht', 'sie sahen'],
      },
      condizionale: {
        it: ['vedrei', 'vedresti', 'vedrebbe', 'vedremmo', 'vedreste', 'vedrebbero'],
        de: ['ich würde sehen', 'du würdest sehen', 'er/sie würde sehen', 'wir würden sehen', 'ihr würdet sehen', 'sie würden sehen'],
      },
      futuroSemplice: {
        it: ['vedrò', 'vedrai', 'vedrà', 'vedremo', 'vedrete', 'vedranno'],
        de: ['ich werde sehen', 'du wirst sehen', 'er/sie wird sehen', 'wir werden sehen', 'ihr werdet sehen', 'sie werden sehen'],
      },
      participioPassato: {
        it: ['visto'],
        de: ['gesehen'],
      },
    },
  },
  {
    infinitive: 'prendere',
    germanInfinitive: 'nehmen',
    group: 'top11_20',
    conjugations: {
      presente: {
        it: ['prendo', 'prendi', 'prende', 'prendiamo', 'prendete', 'prendono'],
        de: ['ich nehme', 'du nimmst', 'er/sie nimmt', 'wir nehmen', 'ihr nehmt', 'sie nehmen'],
      },
      imperfetto: {
        it: ['prendevo', 'prendevi', 'prendeva', 'prendevamo', 'prendevate', 'prendevano'],
        de: ['ich nahm', 'du nahmst', 'er/sie nahm', 'wir nahmen', 'ihr nahmt', 'sie nahmen'],
      },
      condizionale: {
        it: ['prenderei', 'prenderesti', 'prenderebbe', 'prenderemmo', 'prendereste', 'prenderebbero'],
        de: ['ich würde nehmen', 'du würdest nehmen', 'er/sie würde nehmen', 'wir würden nehmen', 'ihr würdet nehmen', 'sie würden nehmen'],
      },
      futuroSemplice: {
        it: ['prenderò', 'prenderai', 'prenderà', 'prenderemo', 'prenderete', 'prenderanno'],
        de: ['ich werde nehmen', 'du wirst nehmen', 'er/sie wird nehmen', 'wir werden nehmen', 'ihr werdet nehmen', 'sie werden nehmen'],
      },
      participioPassato: {
        it: ['preso'],
        de: ['genommen'],
      },
    },
  },
  {
    infinitive: 'parlare',
    germanInfinitive: 'sprechen',
    group: 'top11_20',
    conjugations: {
      presente: {
        it: ['parlo', 'parli', 'parla', 'parliamo', 'parlate', 'parlano'],
        de: ['ich spreche', 'du sprichst', 'er/sie spricht', 'wir sprechen', 'ihr sprecht', 'sie sprechen'],
      },
      imperfetto: {
        it: ['parlavo', 'parlavi', 'parlava', 'parlavamo', 'parlavate', 'parlavano'],
        de: ['ich sprach', 'du sprachst', 'er/sie sprach', 'wir sprachen', 'ihr spracht', 'sie sprachen'],
      },
      condizionale: {
        it: ['parlerei', 'parleresti', 'parlerebbe', 'parleremmo', 'parlereste', 'parlerebbero'],
        de: ['ich würde sprechen', 'du würdest sprechen', 'er/sie würde sprechen', 'wir würden sprechen', 'ihr würdet sprechen', 'sie würden sprechen'],
      },
      futuroSemplice: {
        it: ['parlerò', 'parlerai', 'parlerà', 'parleremo', 'parlerete', 'parleranno'],
        de: ['ich werde sprechen', 'du wirst sprechen', 'er/sie wird sprechen', 'wir werden sprechen', 'ihr werdet sprechen', 'sie werden sprechen'],
      },
      participioPassato: {
        it: ['parlato'],
        de: ['gesprochen'],
      },
    },
  },
  {
    infinitive: 'pensare',
    germanInfinitive: 'denken',
    group: 'top11_20',
    conjugations: {
      presente: {
        it: ['penso', 'pensi', 'pensa', 'pensiamo', 'pensate', 'pensano'],
        de: ['ich denke', 'du denkst', 'er/sie denkt', 'wir denken', 'ihr denkt', 'sie denken'],
      },
      imperfetto: {
        it: ['pensavo', 'pensavi', 'pensava', 'pensavamo', 'pensavate', 'pensavano'],
        de: ['ich dachte', 'du dachtest', 'er/sie dachte', 'wir dachten', 'ihr dachtet', 'sie dachten'],
      },
      condizionale: {
        it: ['penserei', 'penseresti', 'penserebbe', 'penseremmo', 'pensereste', 'penserebbero'],
        de: ['ich würde denken', 'du würdest denken', 'er/sie würde denken', 'wir würden denken', 'ihr würdet denken', 'sie würden denken'],
      },
      futuroSemplice: {
        it: ['penserò', 'penserai', 'penserà', 'penseremo', 'penserete', 'penseranno'],
        de: ['ich werde denken', 'du wirst denken', 'er/sie wird denken', 'wir werden denken', 'ihr werdet denken', 'sie werden denken'],
      },
      participioPassato: {
        it: ['pensato'],
        de: ['gedacht'],
      },
    },
  },
  {
    infinitive: 'trovare',
    germanInfinitive: 'finden',
    group: 'top11_20',
    conjugations: {
      presente: {
        it: ['trovo', 'trovi', 'trova', 'troviamo', 'trovate', 'trovano'],
        de: ['ich finde', 'du findest', 'er/sie findet', 'wir finden', 'ihr findet', 'sie finden'],
      },
      imperfetto: {
        it: ['trovavo', 'trovavi', 'trovava', 'trovavamo', 'trovavate', 'trovavano'],
        de: ['ich fand', 'du fandest', 'er/sie fand', 'wir fanden', 'ihr fandet', 'sie fanden'],
      },
      condizionale: {
        it: ['troverei', 'troveresti', 'troverebbe', 'troveremmo', 'trovereste', 'troverebbero'],
        de: ['ich würde finden', 'du würdest finden', 'er/sie würde finden', 'wir würden finden', 'ihr würdet finden', 'sie würden finden'],
      },
      futuroSemplice: {
        it: ['troverò', 'troverai', 'troverà', 'troveremo', 'troverete', 'troveranno'],
        de: ['ich werde finden', 'du wirst finden', 'er/sie wird finden', 'wir werden finden', 'ihr werdet finden', 'sie werden finden'],
      },
      participioPassato: {
        it: ['trovato'],
        de: ['gefunden'],
      },
    },
  },
  {
    infinitive: 'sentire',
    germanInfinitive: 'fühlen, hören',
    group: 'top11_20',
    conjugations: {
      presente: {
        it: ['sento', 'senti', 'sente', 'sentiamo', 'sentite', 'sentono'],
        de: ['ich fühle', 'du fühlst', 'er/sie fühlt', 'wir fühlen', 'ihr fühlt', 'sie fühlen'],
      },
      imperfetto: {
        it: ['sentivo', 'sentivi', 'sentiva', 'sentivamo', 'sentivate', 'sentivano'],
        de: ['ich fühlte', 'du fühltest', 'er/sie fühlte', 'wir fühlten', 'ihr fühltet', 'sie fühlten'],
      },
      condizionale: {
        it: ['sentirei', 'sentiresti', 'sentirebbe', 'sentiremmo', 'sentireste', 'sentirebbero'],
        de: ['ich würde fühlen', 'du würdest fühlen', 'er/sie würde fühlen', 'wir würden fühlen', 'ihr würdet fühlen', 'sie würden fühlen'],
      },
      futuroSemplice: {
        it: ['sentirò', 'sentirai', 'sentirà', 'sentiremo', 'sentirete', 'sentiranno'],
        de: ['ich werde fühlen', 'du wirst fühlen', 'er/sie wird fühlen', 'wir werden fühlen', 'ihr werdet fühlen', 'sie werden fühlen'],
      },
      participioPassato: {
        it: ['sentito'],
        de: ['gefühlt'],
      },
    },
  },
  {
    infinitive: 'mettere',
    germanInfinitive: 'setzen, stellen, legen',
    group: 'top11_20',
    conjugations: {
      presente: {
        it: ['metto', 'metti', 'mette', 'mettiamo', 'mettete', 'mettono'],
        de: ['ich setze', 'du setzt', 'er/sie setzt', 'wir setzen', 'ihr setzt', 'sie setzen'],
      },
      imperfetto: {
        it: ['mettevo', 'mettevi', 'metteva', 'mettevamo', 'mettevate', 'mettevano'],
        de: ['ich setzte', 'du setztest', 'er/sie setzte', 'wir setzten', 'ihr setztet', 'sie setzten'],
      },
      condizionale: {
        it: ['metterei', 'metteresti', 'metterebbe', 'metteremmo', 'mettereste', 'metterebbero'],
        de: ['ich würde setzen', 'du würdest setzen', 'er/sie würde setzen', 'wir würden setzen', 'ihr würdet setzen', 'sie würden setzen'],
      },
      futuroSemplice: {
        it: ['metterò', 'metterai', 'metterà', 'metteremo', 'metterete', 'metteranno'],
        de: ['ich werde setzen', 'du wirst setzen', 'er/sie wird setzen', 'wir werden setzen', 'ihr werdet setzen', 'sie werden setzen'],
      },
      participioPassato: {
        it: ['messo'],
        de: ['gesetzt'],
      },
    },
  },
  {
    infinitive: 'credere',
    germanInfinitive: 'glauben',
    group: 'top11_20',
    conjugations: {
      presente: {
        it: ['credo', 'credi', 'crede', 'crediamo', 'credete', 'credono'],
        de: ['ich glaube', 'du glaubst', 'er/sie glaubt', 'wir glauben', 'ihr glaubt', 'sie glauben'],
      },
      imperfetto: {
        it: ['credevo', 'credevi', 'credeva', 'credevamo', 'credevate', 'credevano'],
        de: ['ich glaubte', 'du glaubtest', 'er/sie glaubte', 'wir glaubten', 'ihr glaubtet', 'sie glaubten'],
      },
      condizionale: {
        it: ['crederei', 'crederesti', 'crederebbe', 'crederemmo', 'credereste', 'crederebbero'],
        de: ['ich würde glauben', 'du würdest glauben', 'er/sie würde glauben', 'wir würden glauben', 'ihr würdet glauben', 'sie würden glauben'],
      },
      futuroSemplice: {
        it: ['crederò', 'crederai', 'crederà', 'crederemo', 'crederete', 'crederanno'],
        de: ['ich werde glauben', 'du wirst glauben', 'er/sie wird glauben', 'wir werden glauben', 'ihr werdet glauben', 'sie werden glauben'],
      },
      participioPassato: {
        it: ['creduto'],
        de: ['geglaubt'],
      },
    },
  },
];

export const ALL_VERBS = [...TOP_10_VERBS, ...TOP_11_20_VERBS];

/**
 * Get verbs by group
 */
export function getVerbsByGroup(groupValue) {
  return ALL_VERBS.filter((v) => v.group === groupValue);
}

/**
 * Look up a verb's conjugation from the verified table.
 */
export function lookupConjugation(infinitive) {
  return ALL_VERBS.find((v) => v.infinitive.toLowerCase() === infinitive.toLowerCase()) || null;
}

/**
 * Calculate how many cards will be generated for given selections.
 */
export function calculateCardCount(selectedGroups, selectedForms) {
  let verbCount = 0;
  selectedGroups.forEach((group) => {
    verbCount += getVerbsByGroup(group).length;
  });

  let cardsPerVerb = 0;
  selectedForms.forEach((formValue) => {
    const form = CONJUGATION_FORMS.find((f) => f.value === formValue);
    if (form) {
      cardsPerVerb += form.personsCount;
    }
  });

  return verbCount * cardsPerVerb;
}

/**
 * Generate verb card data objects for given selections.
 * Each card includes both Italian conjugation AND German translation.
 */
export function generateVerbCards(selectedGroups, selectedForms) {
  const cards = [];
  const now = new Date().toISOString();

  selectedGroups.forEach((groupValue) => {
    const verbs = getVerbsByGroup(groupValue);

    verbs.forEach((verb) => {
      selectedForms.forEach((formValue) => {
        const form = CONJUGATION_FORMS.find((f) => f.value === formValue);
        if (!form) return;

        const conjugations = verb.conjugations[formValue];
        if (!conjugations) return;

        if (formValue === 'participioPassato') {
          cards.push({
            type: 'verb',
            baseVerb: verb.infinitive,
            germanInfinitive: verb.germanInfinitive,
            verbGroup: groupValue,
            conjugationForm: formValue,
            conjugationFormLabel: form.label,
            conjugationFormGermanLabel: form.germanLabel,
            targetPerson: null,
            targetPersonLabel: 'Participio Passato',
            targetPersonGermanLabel: form.germanLabel,
            targetFormDescription: `Participio Passato`,
            targetFormDescriptionGerman: `${form.germanLabel}`,
            conjugatedAnswer: conjugations.it[0],
            germanConjugation: conjugations.de[0],
            createdAt: now,
          });
        } else {
          PERSONS.forEach((person, personIndex) => {
            cards.push({
              type: 'verb',
              baseVerb: verb.infinitive,
              germanInfinitive: verb.germanInfinitive,
              verbGroup: groupValue,
              conjugationForm: formValue,
              conjugationFormLabel: form.label,
              conjugationFormGermanLabel: form.germanLabel,
              targetPerson: person.value,
              targetPersonLabel: person.label,
              targetPersonGermanLabel: person.germanLabel,
              targetFormDescription: `${person.label}, ${form.label}`,
              targetFormDescriptionGerman: `${person.germanLabel}, ${form.germanLabel}`,
              conjugatedAnswer: conjugations.it[personIndex],
              germanConjugation: conjugations.de[personIndex],
              createdAt: now,
            });
          });
        }
      });
    });
  });

  return cards;
}

/**
 * Build a unique key for a verb card to detect duplicates.
 * Key: baseVerb + conjugationForm + targetPerson
 */
export function getVerbCardKey(card) {
  return `${card.baseVerb}|${card.conjugationForm}|${card.targetPerson || 'pp'}`;
}